/**
 * Left column: one row per {@link flattenVisibleScheduleRows} entry—project headers and
 * nested group rows with expand/collapse and “add group/subgroup” affordances.
 */
import { useEffect, useRef, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IoAdd, IoTrashOutline } from "react-icons/io5";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../config";
import { SCHEDULE_ROW_HEIGHT } from "./constants";
import {
  getScheduleRowCellBorderClass,
  getScheduleRowStripeClass,
} from "./model/scheduleRowStriping";
import {
  HOVER_BG_PRIMARY_1,
  SCHEDULE_HEADER_CELL_BORDER,
  SCHEDULE_HEADER_PANEL_DIVIDER_BORDER_THICKNESS,
  SCHEDULE_PROJECT_CELL_BG,
} from "./style";

const iconSx = { fontSize: 18, color: "#6B7280" };

/**
 * Left rail: project and group rows with expand/collapse and group CRUD affordances.
 *
 * @param {Object} props
 * @param {number} props.width Panel width in pixels.
 * @param {Array<Object>} props.scheduleRows Flat rows from {@link flattenVisibleScheduleRows}.
 * @param {number} [props.rowHeight] Base row height (defaults to {@link SCHEDULE_ROW_HEIGHT}).
 * @param {Map<string, number>} props.rowHeightsById Row id → stacked height from overlap layout.
 * @param {Set<string>} props.expandedProjectIds
 * @param {Set<string>} props.expandedGroupIds
 * @param {(projectId: string) => void} props.onToggleProject
 * @param {(groupId: string) => void} props.onToggleGroup
 * @param {(projectId: string, parentGroupId?: string|null) => void} props.onAddGroup
 * @param {(groupId: string) => void} props.onDeleteGroup
 * @param {(groupId: string, name: string) => void|Promise<void>} props.onRenameGroup
 * @param {() => void} props.onShowAllProjects Expand every project row.
 * @param {string} [props.projectSearchQuery] Active schedule search text (empty-state copy).
 * @param {import('react').RefObject<HTMLElement>} props.scrollContainerRef Shared vertical scroll container.
 * @param {import('react').UIEventHandler<HTMLElement>} props.onScroll Scroll sync handler.
 * @returns {JSX.Element}
 */
export default function ScheduleProjectsPanel({
  width,
  scheduleRows,
  rowHeight = SCHEDULE_ROW_HEIGHT,
  rowHeightsById,
  expandedProjectIds,
  expandedGroupIds,
  onToggleProject,
  onToggleGroup,
  onAddGroup,
  onDeleteGroup,
  onRenameGroup,
  onShowAllProjects,
  projectSearchQuery = "",
  scrollContainerRef,
  onScroll,
}) {
  const hasActiveProjectSearch = Boolean(projectSearchQuery.trim());
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const inputRef = useRef(null);

  /** Focus and select the inline input as soon as a group enters edit mode. */
  useEffect(() => {
    if (editingGroupId == null) {
      return;
    }
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editingGroupId]);

  /** Start inline editing for a group row label. */
  const beginGroupRename = (row) => {
    if (row.rowType !== "group" || !row.groupId) {
      return;
    }
    setEditingGroupId(row.groupId);
    setDraftName(row.label ?? "");
  };

  /** Commit a changed group name on blur/Enter; empty names cancel the edit. */
  const commitGroupRename = () => {
    const groupId = editingGroupId;
    const nextName = draftName.trim();
    setEditingGroupId(null);
    setDraftName("");
    if (groupId == null || !nextName) {
      return;
    }
    onRenameGroup?.(groupId, nextName);
  };

  /** Cancel the current inline edit and restore the visible group label. */
  const cancelGroupRename = () => {
    setEditingGroupId(null);
    setDraftName("");
  };

  return (
    <div
      ref={scrollContainerRef}
      className={`h-full min-h-0 shrink-0 overflow-x-hidden overflow-y-hidden ${SCHEDULE_PROJECT_CELL_BG} border-r-[${SCHEDULE_HEADER_PANEL_DIVIDER_BORDER_THICKNESS}px] ${SCHEDULE_HEADER_CELL_BORDER}`}
      style={{ width }}
      onScroll={onScroll}
    >
      {scheduleRows.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center px-4 text-center text-xs text-gray-500">
          <p className="font-medium text-gray-600">
            {hasActiveProjectSearch
              ? "No schedule rows match your search"
              : "No Projects to Show"}
          </p>
          {!hasActiveProjectSearch ? (
            <button
              type="button"
              className="mt-1 text-blue-600 underline hover:text-blue-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400"
              onClick={() => onShowAllProjects?.()}
            >
              Unhide Empty Projects
            </button>
          ) : null}
        </div>
      ) : null}
      {scheduleRows.map((row) => {
        const visualRowHeight = rowHeightsById?.get(row.id) ?? rowHeight;
        const expanded =
          row.rowType === "project"
            ? expandedProjectIds.has(row.projectId)
            : expandedGroupIds.has(row.groupId);

        const showToggle = row.hasChildren;
        const paddingLeft = 0 + row.depth * 14;

        return (
          <div
            key={row.id}
            className={`group text-xs text-gray-800 ${getScheduleRowCellBorderClass(row, { omitRight: true })} ${getScheduleRowStripeClass(row)}`}
            style={{
              minHeight: visualRowHeight,
              height: visualRowHeight,
              paddingLeft,
            }}
          >
            <div
              className="flex items-center gap-1"
              style={{ height: rowHeight, minHeight: rowHeight }}
            >
              {showToggle ? (
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-label={expanded ? "Collapse" : "Expand"}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-500 ${HOVER_BG_PRIMARY_1}`}
                  onClick={() =>
                    row.rowType === "project"
                      ? onToggleProject(row.projectId)
                      : onToggleGroup(row.groupId)
                  }
                >
                  {expanded ? (
                    <ExpandMoreIcon sx={iconSx} />
                  ) : (
                    <ChevronRightIcon sx={iconSx} />
                  )}
                </button>
              ) : (
                <span className="inline-block w-7 shrink-0" />
              )}

              {row.rowType === "group" && String(editingGroupId) === String(row.groupId) ? (
                <input
                  ref={inputRef}
                  className="min-w-0 flex-1 caret-black rounded border border-primary/40 bg-white px-1 py-0.5 font-medium leading-snug text-gray-900 outline-none focus:ring-1 focus:ring-primary/40"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={commitGroupRename}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      cancelGroupRename();
                    }
                  }}
                />
              ) : row.rowType === "group" ? (
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate rounded text-left font-medium leading-snug hover:bg-white/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
                  title={PLANNING_TOOLTIP_MESSAGES.clickToEditGroupName}
                  onClick={() => beginGroupRename(row)}
                >
                  {row.label}
                </button>
              ) : (
                <span
                  className="min-w-0 flex-1 truncate font-medium leading-snug"
                  title={row.label}
                >
                  {row.label}
                </span>
              )}

              <div className="ml-1 flex shrink-0 items-center gap-0.5">
                {row.rowType === "group" && row.groupId ? (
                  <button
                    type="button"
                    className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-50 hover:text-red-700"
                    aria-label="Delete group"
                    title={PLANNING_TOOLTIP_MESSAGES.deleteGroup}
                    onClick={() => onDeleteGroup?.(row.groupId)}
                  >
                    <IoTrashOutline className="h-4 w-4" aria-hidden />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`inline-flex h-5 w-5 items-center justify-center rounded text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 ${HOVER_BG_PRIMARY_1} hover:text-gray-700`}
                  aria-label={
                    row.rowType === "project" ? "Add Group" : "Add Subgroup"
                  }
                  onClick={() =>
                    onAddGroup?.(
                      row.projectId,
                      row.rowType === "group" ? row.groupId : null
                    )
                  }
                >
                  <IoAdd className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
